import type { StorefrontAppConfig, StorefrontEventName } from '@xshop/shared';
import { CONSENT_EVENT, readConsent } from '../consentAnswer';
import { storeHeaders } from '../../api/client';
import type { AppEventDetail, BrowserApp } from './browserApp';
import { clarityApp } from './clarity';
import { klaviyoApp } from './klaviyo';
import { tidioApp } from './tidio';
import { crispApp } from './crisp';
import { tawkApp } from './tawk';

// The browser part of the store's connected apps (Clarity, Klaviyo, chat widgets). Like the ad pixels, nothing loads
// for a shopper who refused cookies, and a refusal changed to an acceptance loads them then.

const CONFIG_PATH = '/api/v1/apps/storefront';
// The most events held while the config loads; a page that never loads it cannot grow without bound.
const MAX_PENDING = 50;

// One browser script per app handle; an app without one is server-side only.
const APPS: Record<string, BrowserApp> = {
  'microsoft-clarity': clarityApp,
  klaviyo: klaviyoApp,
  tidio: tidioApp,
  crisp: crispApp,
  'tawk-to': tawkApp
};

let installed: StorefrontAppConfig[] | null = null;
let waiting = false;
const pending: Array<{ name: StorefrontEventName; detail: AppEventDetail }> = [];

// Reports whether the shopper refused cookies; a shop that never asks has no answer and loads.
const refused = (): boolean => readConsent() === 'rejected';

// Reads the store's connected apps once per page load; a failure loads none.
const loadConfig = (): Promise<StorefrontAppConfig[]> => fetch(CONFIG_PATH, { headers: storeHeaders() })
  .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
  .then((json: { data?: StorefrontAppConfig[] }) => (Array.isArray(json.data) ? json.data : []))
  .catch(() => []);

// Sends one event to every installed app script; a broken vendor never stops the others.
const fanOut = (name: StorefrontEventName, detail: AppEventDetail, apps: StorefrontAppConfig[]): void => {
  for (const app of apps) {
    try {
      APPS[app.handle]?.track(name, detail);
    } catch {
      // A vendor script's failure stays out of the store.
    }
  }
};

// Waits for a shopper who refused to change their mind, then loads. Listens once.
const waitForConsent = (): void => {
  if (typeof window === 'undefined' || waiting) return;
  waiting = true;
  window.addEventListener(CONSENT_EVENT, () => {
    waiting = false;
    if (!refused()) void startApps();
  }, { once: true });
};

// Loads every connected app's script, then sends the events that fired while the config was loading.
export async function startApps(): Promise<void> {
  if (typeof window === 'undefined' || installed) return;
  if (refused()) return void waitForConsent();
  const apps = await loadConfig();
  installed = apps;
  for (const app of apps) {
    try {
      APPS[app.handle]?.install(app.settings, readConsent() === 'accepted');
    } catch {
      // An app that fails to load is simply absent.
    }
  }
  for (const queued of pending.splice(0)) fanOut(queued.name, queued.detail, apps);
}

// Sends one storefront event to the connected apps' scripts, queued until they load; dropped when refused.
export function fireAppEvent(name: StorefrontEventName, detail: AppEventDetail): void {
  try {
    if (typeof window === 'undefined' || refused()) return;
    if (installed) fanOut(name, detail, installed);
    else if (pending.length < MAX_PENDING) pending.push({ name, detail });
  } catch {
    // Tracking never surfaces an error into the store.
  }
}
